import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { FileText, FileSpreadsheet, Library, Check, ChevronsUpDown } from 'lucide-react';
import { JournalEntryAuditDialog, EntryAuditButton } from '../components/JournalEntryAuditDialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };

/** Popover-combo reutilizable para elegir una cuenta imputable */
function AccountPicker({ accounts, value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const selected = accounts.find(a => a.code === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid={testId}>
          <span className="truncate">
            {selected ? `${selected.code} · ${selected.name}` : (placeholder || 'Buscá una cuenta…')}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[28rem] p-0" align="start">
        <Command filter={(v, s) => v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0}>
          <CommandInput placeholder="Buscar por código o nombre…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              {accounts.map(a => (
                <CommandItem
                  key={a.code}
                  value={`${a.code} ${a.name}`}
                  onSelect={() => { onChange(a.code); setOpen(false); }}
                  data-testid={`${testId}-option-${a.code}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === a.code ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="font-mono text-xs mr-2">{a.code}</span>{a.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function AccountingLedger() {
  useOutletContext();
  const [accounts, setAccounts] = useState([]);
  const [mode, setMode] = useState('single'); // 'single' | 'range'
  const [accountCode, setAccountCode] = useState('');
  const [codeFrom, setCodeFrom] = useState('');
  const [codeTo, setCodeTo] = useState('');
  const [range, setRange] = useState({ date_from: '', date_to: '' });
  const [data, setData] = useState(null);          // modo single
  const [rangeData, setRangeData] = useState(null); // modo range
  const [loading, setLoading] = useState(false);
  const [auditEntryId, setAuditEntryId] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/accounting/accounts?only_postable=true`, { withCredentials: true })
      .then(r => setAccounts(r.data)).catch(() => toast.error('Error al cargar cuentas'));
  }, []);

  const loadSingle = async (code = accountCode) => {
    if (!code) { toast.error('Elegí una cuenta'); return; }
    setLoading(true);
    setRangeData(null);
    try {
      const params = { account_code: code };
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const r = await axios.get(`${BACKEND_URL}/api/accounting/ledger`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar Libro Mayor'); } finally { setLoading(false); }
  };

  const loadRange = async () => {
    if (!codeFrom || !codeTo) { toast.error('Elegí cuenta desde y cuenta hasta'); return; }
    setLoading(true);
    setData(null);
    try {
      const params = { code_from: codeFrom, code_to: codeTo };
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const r = await axios.get(`${BACKEND_URL}/api/accounting/ledger-range`, { params, withCredentials: true });
      setRangeData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cargar Mayor por rango'); } finally { setLoading(false); }
  };

  const apply = () => (mode === 'range' ? loadRange() : loadSingle());

  const buildUrlSingle = (ext) => {
    const url = new URL(`${BACKEND_URL}/api/accounting/ledger.${ext}`);
    url.searchParams.set('account_code', accountCode);
    if (range.date_from) url.searchParams.set('date_from', range.date_from);
    if (range.date_to) url.searchParams.set('date_to', range.date_to);
    return url.toString();
  };
  const buildUrlRange = (ext) => {
    const url = new URL(`${BACKEND_URL}/api/accounting/ledger-range.${ext}`);
    url.searchParams.set('code_from', codeFrom);
    url.searchParams.set('code_to', codeTo);
    if (range.date_from) url.searchParams.set('date_from', range.date_from);
    if (range.date_to) url.searchParams.set('date_to', range.date_to);
    return url.toString();
  };

  const canExport = mode === 'range' ? (codeFrom && codeTo) : !!accountCode;
  const buildUrl = (ext) => (mode === 'range' ? buildUrlRange(ext) : buildUrlSingle(ext));

  return (
    <div className="space-y-6" data-testid="accounting-ledger-page">
      <div className="flex items-center gap-3">
        <Library className="w-7 h-7 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold">Libro Mayor</h1>
          <p className="text-sm text-muted-foreground">Movimientos y saldo por cuenta — individual o por rango</p>
        </div>
      </div>

      <Card><CardContent className="pt-6">
        {/* Toggle modo */}
        <div className="flex items-center gap-2 mb-4">
          <Button
            size="sm"
            variant={mode === 'single' ? 'default' : 'outline'}
            onClick={() => setMode('single')}
            data-testid="ledger-mode-single"
          >Una cuenta</Button>
          <Button
            size="sm"
            variant={mode === 'range' ? 'default' : 'outline'}
            onClick={() => setMode('range')}
            data-testid="ledger-mode-range"
          >Rango de cuentas</Button>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {mode === 'single' ? (
            <div className="min-w-80">
              <Label className="text-xs">Cuenta *</Label>
              <AccountPicker
                accounts={accounts}
                value={accountCode}
                onChange={(c) => { setAccountCode(c); loadSingle(c); }}
                testId="ledger-account-select"
              />
            </div>
          ) : (
            <>
              <div className="min-w-72">
                <Label className="text-xs">Cuenta Desde *</Label>
                <AccountPicker
                  accounts={accounts}
                  value={codeFrom}
                  onChange={setCodeFrom}
                  placeholder="Cuenta desde…"
                  testId="ledger-code-from"
                />
              </div>
              <div className="min-w-72">
                <Label className="text-xs">Cuenta Hasta *</Label>
                <AccountPicker
                  accounts={accounts}
                  value={codeTo}
                  onChange={setCodeTo}
                  placeholder="Cuenta hasta…"
                  testId="ledger-code-to"
                />
              </div>
            </>
          )}
          <div><Label className="text-xs">Desde</Label><Input type="date" value={range.date_from} onChange={e => setRange({ ...range, date_from: e.target.value })} data-testid="ledger-date-from" /></div>
          <div><Label className="text-xs">Hasta</Label><Input type="date" value={range.date_to} onChange={e => setRange({ ...range, date_to: e.target.value })} data-testid="ledger-date-to" /></div>
          <Button onClick={apply} data-testid="ledger-apply-btn">Aplicar</Button>
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" disabled={!canExport} onClick={() => window.open(buildUrl('pdf'), '_blank')} data-testid="ledger-pdf"><FileText className="w-4 h-4 mr-2" />PDF</Button>
            <Button variant="outline" disabled={!canExport} onClick={() => window.open(buildUrl('xlsx'), '_blank')} data-testid="ledger-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
          </div>
        </div>
      </CardContent></Card>

      {loading && <p className="text-center py-8 text-muted-foreground">Cargando…</p>}

      {/* Modo single — Mayor de una cuenta */}
      {!loading && mode === 'single' && data && (
        <Card><CardContent className="pt-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="font-mono font-semibold text-lg">{data.account.code}</span>
            <span className="font-semibold">{data.account.name}</span>
            <Badge variant="outline">{data.account.type}</Badge>
            <Badge variant="outline">{data.account.nature}</Badge>
          </div>
          <Table data-testid="ledger-table">
            <TableHeader><TableRow>
              <TableHead className="w-20 text-center">Asiento</TableHead><TableHead className="w-28">Fecha</TableHead><TableHead>Detalle</TableHead>
              <TableHead className="text-right w-32">Debe</TableHead><TableHead className="text-right w-32">Haber</TableHead><TableHead className="text-right w-36">Saldo</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              <TableRow className="bg-muted/40 italic">
                <TableCell colSpan={3}>Saldo inicial</TableCell><TableCell /><TableCell />
                <TableCell className="text-right font-medium" data-testid="ledger-opening">{fmtMoney(data.opening_balance)}</TableCell>
              </TableRow>
              {data.rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="text-center font-mono text-xs">#{r.entry_number}</TableCell>
                  <TableCell>{fmtDate(r.date)}</TableCell>
                  <TableCell>
                    <span className="block">{r.description}</span>
                    <EntryAuditButton entryId={r.entry_id} onOpen={setAuditEntryId} testId={`ledger-audit-btn-${r.entry_number}`} />
                  </TableCell>
                  <TableCell className="text-right">{r.debit > 0 ? fmtMoney(r.debit) : ''}</TableCell>
                  <TableCell className="text-right">{r.credit > 0 ? fmtMoney(r.credit) : ''}</TableCell>
                  <TableCell className="text-right font-medium">{fmtMoney(r.balance)}</TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Sin movimientos en el período</TableCell></TableRow>}
              <TableRow className="bg-muted font-bold border-t-2">
                <TableCell colSpan={3}>TOTALES</TableCell>
                <TableCell className="text-right">{fmtMoney(data.total_debit)}</TableCell>
                <TableCell className="text-right">{fmtMoney(data.total_credit)}</TableCell>
                <TableCell className="text-right" data-testid="ledger-closing">{fmtMoney(data.closing_balance)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      {/* Modo range — N bloques */}
      {!loading && mode === 'range' && rangeData && (
        <div className="space-y-4" data-testid="ledger-range-view">
          <Card>
            <CardContent className="pt-4 pb-4 flex flex-wrap items-center gap-6">
              <div>
                <div className="text-xs text-muted-foreground uppercase">Rango</div>
                <div className="font-mono font-semibold">{rangeData.code_from} → {rangeData.code_to}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase">Cuentas con movimientos</div>
                <div className="font-bold text-lg">{rangeData.accounts_count}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase">Total Debe</div>
                <div className="font-bold text-indigo-700">{fmtMoney(rangeData.total_debit)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase">Total Haber</div>
                <div className="font-bold text-indigo-700">{fmtMoney(rangeData.total_credit)}</div>
              </div>
            </CardContent>
          </Card>

          {(rangeData.blocks || []).length === 0 && (
            <Card><CardContent className="py-10 text-center text-muted-foreground">
              Sin movimientos en el rango seleccionado
            </CardContent></Card>
          )}

          {(rangeData.blocks || []).map((b, idx) => (
            <Card key={b.account.code} data-testid={`ledger-range-block-${b.account.code}`}>
              <CardContent className="pt-5">
                <div className="mb-3 flex items-center gap-3 border-b pb-2">
                  <span className="font-mono font-semibold text-base">{b.account.code}</span>
                  <span className="font-semibold">{b.account.name}</span>
                  <Badge variant="outline">{b.account.type}</Badge>
                  <Badge variant="outline">{b.account.nature}</Badge>
                  <span className="ml-auto text-xs text-muted-foreground">#{idx + 1} de {rangeData.accounts_count}</span>
                </div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-20 text-center">Asiento</TableHead>
                    <TableHead className="w-28">Fecha</TableHead>
                    <TableHead>Detalle</TableHead>
                    <TableHead className="text-right w-32">Debe</TableHead>
                    <TableHead className="text-right w-32">Haber</TableHead>
                    <TableHead className="text-right w-36">Saldo</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    <TableRow className="bg-muted/40 italic">
                      <TableCell colSpan={3}>Saldo inicial</TableCell><TableCell /><TableCell />
                      <TableCell className="text-right font-medium">{fmtMoney(b.opening_balance)}</TableCell>
                    </TableRow>
                    {b.rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-center font-mono text-xs">#{r.entry_number}</TableCell>
                        <TableCell>{fmtDate(r.date)}</TableCell>
                        <TableCell>
                          <span className="block">{r.description}</span>
                          <EntryAuditButton entryId={r.entry_id} onOpen={setAuditEntryId} testId={`ledger-range-audit-${b.account.code}-${r.entry_number}`} />
                        </TableCell>
                        <TableCell className="text-right">{r.debit > 0 ? fmtMoney(r.debit) : ''}</TableCell>
                        <TableCell className="text-right">{r.credit > 0 ? fmtMoney(r.credit) : ''}</TableCell>
                        <TableCell className="text-right font-medium">{fmtMoney(r.balance)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted font-bold border-t-2">
                      <TableCell colSpan={3}>TOTALES CUENTA</TableCell>
                      <TableCell className="text-right">{fmtMoney(b.total_debit)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(b.total_credit)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(b.closing_balance)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <JournalEntryAuditDialog entryId={auditEntryId} onClose={() => setAuditEntryId(null)} />
    </div>
  );
}
